import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { createParam } from "solito";
import { useFocusEffect } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { errorHandling } from "../../lib/apollo/utils";
import { FilterLoadState } from "../../lib/filtering/types";
import { isLoading, selectFilter } from "../../lib/filtering/utils";
import { useBoolVariation } from "../../lib/flag";
import { useAlerts } from "../../provider/alerts";
import { useIssueEvents } from "../../provider/issue-events";
import { CaseList } from "./components/CaseList";
import { getIssuesQueryV2 } from "./utils/queries";
import type { CaseListFilters } from "./utils/types";
import { filterMap, filterOptionIds, pageSize } from "./utils/utils";
const {
  useParam
} = createParam<{
  id: string;
}>();
const getFilterKey = (read: boolean, unread: boolean, closed: boolean) => `read:${read}-unread:${unread}-closed:${closed}`;
type CasesScreenProps = {
  filterState?: CaseListFilters & {
    setFilters: (filters: CaseListFilters) => void;
  };
  markKeepOpen?: () => void;
  source?: string;
};
export const CasesScreen = (props: CasesScreenProps) => {
  const showCasesPanelV2 = useBoolVariation(Flag.NEW_CASELIST_UI, false);
  if (showCasesPanelV2) {
    return <CasesScreenV2 {...props} />;
  }
  return <CasesScreenLegacy data-sentry-element="CasesScreenLegacy" data-sentry-component="CasesScreen" data-sentry-source-file="screen.tsx" />;
};

/**
 * Uses a flatlist as the base screen because of headerTitleLarge requiring a
 * scrollview or flat list as its direct children and when a flatlist is inside
 * of a scrollView it breaks the scroll / distance end threshold detection.
 */
const CasesScreenLegacy = () => {
  const [caseId] = useParam("id");
  const [filter, setFilter] = useState(filterOptionIds.open);
  const [endReached, setEndReached] = useState(false);
  const {
    showAlert
  } = useAlerts();
  const [filtersLoaded, setFiltersLoaded] = useState<Record<string, FilterLoadState>>({
    [filterOptionIds.open]: FilterLoadState.LOADING,
    [filterOptionIds.unread]: FilterLoadState.NOT_LOADED,
    [filterOptionIds.closed]: FilterLoadState.NOT_LOADED
  });
  const {
    refetchUnreadIssuesCount
  } = useIssueEvents();
  const {
    data,
    refetch,
    fetchMore,
    networkStatus
  } = useQuery(getIssuesQueryV2, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    refetchWritePolicy: "merge",
    variables: filterMap[filter]
  });
  useFocusEffect(useCallback(() => {
    void refetch();
  }, [refetch]));
  const doRefresh = useCallback(async () => {
    try {
      setEndReached(false);
      await refetch();
      refetchUnreadIssuesCount();
    } catch (error) {
      console.error("Error refetching issues:", error);
      showAlert({
        type: "error",
        message: "Something went wrong while refreshing your cases. Please try again in a moment."
      });
    }
  }, [refetch, refetchUnreadIssuesCount, showAlert]);
  const handleSelectFilter = useCallback((id: string) => {
    selectFilter(id, setFiltersLoaded, setEndReached, setFilter);
  }, []);
  const fetchMoreItems = useCallback(async () => {
    try {
      const lastIssuePosition = data?.queryIssuesV2?.length ?? 0;
      if (lastIssuePosition >= pageSize && !endReached) {
        const resp = await fetchMore({
          variables: {
            after: lastIssuePosition
          }
        });
        if (resp.data.queryIssuesV2?.length === 0) {
          setEndReached(true);
        }
      }
    } catch (error) {
      console.error("Error fetching more issues:", error);
      showAlert({
        type: "error",
        message: "We couldn\u2019t load more cases. Please try again in a moment."
      });
    }
  }, [data?.queryIssuesV2, endReached, fetchMore, showAlert]);
  const issues = data?.queryIssuesV2 ?? [];
  return <CaseList issues={issues} fetchMore={fetchMoreItems} loading={isLoading(networkStatus, filtersLoaded[filter])} onRefresh={doRefresh} selectedFilter={filter} onTabPress={handleSelectFilter} endReached={endReached} currentIssueId={caseId} data-sentry-element="CaseList" data-sentry-component="CasesScreenLegacy" data-sentry-source-file="screen.tsx" />;
};
const CasesScreenV2 = ({
  filterState,
  markKeepOpen,
  source = "cases_native_list"
}: CasesScreenProps) => {
  const [caseId] = useParam("id");
  const [endReached, setEndReached] = useState(false);
  const {
    showAlert
  } = useAlerts();

  // Local filter state fallback (used on mobile where there is no CasesPanelProvider)
  const [localRead, setLocalRead] = useState(true);
  const [localUnread, setLocalUnread] = useState(true);
  const [localClosed, setLocalClosed] = useState(false);
  const showRead = filterState?.showRead ?? localRead;
  const showUnread = filterState?.showUnread ?? localUnread;
  const showClosed = filterState?.showClosed ?? localClosed;
  const setFilters = useMemo(() => filterState?.setFilters ?? ((filters: CaseListFilters) => {
    setLocalRead(filters.showRead);
    setLocalUnread(filters.showUnread);
    setLocalClosed(filters.showClosed);
  }), [filterState?.setFilters]);
  const {
    refetchUnreadIssuesCount
  } = useIssueEvents();
  const [filtersLoaded, setFiltersLoaded] = useState<Record<string, FilterLoadState>>(() => ({
    [getFilterKey(showRead, showUnread, showClosed)]: FilterLoadState.LOADING
  }));
  const currentFilterKey = getFilterKey(showRead, showUnread, showClosed);
  const currentFilterState = filtersLoaded[currentFilterKey] ?? FilterLoadState.NOT_LOADED;
  const queryVars = {
    read: showRead,
    unread: showUnread,
    open: showRead || showUnread,
    closed: showClosed,
    first: pageSize,
    after: 0
  };
  const {
    data,
    refetch,
    fetchMore,
    networkStatus
  } = useQuery(getIssuesQueryV2, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    refetchWritePolicy: "merge",
    variables: queryVars,
    onError: () => {
      showAlert({
        type: "error",
        message: "Something went wrong while loading your cases. Please try again in a moment."
      });
    }
  });
  useFocusEffect(useCallback(() => {
    void refetch();
  }, [refetch]));
  const doRefresh = useCallback(async () => {
    try {
      setEndReached(false);
      await refetch();
      refetchUnreadIssuesCount();
    } catch (error) {
      console.error("Error refetching issues:", error);
      showAlert({
        type: "error",
        message: "Something went wrong while refreshing your cases. Please try again in a moment."
      });
    }
  }, [refetch, refetchUnreadIssuesCount, showAlert]);
  const fetchMoreItems = useCallback(async () => {
    try {
      const lastIssuePosition = data?.queryIssuesV2?.length ?? 0;
      if (lastIssuePosition >= pageSize && !endReached) {
        const resp = await fetchMore({
          variables: {
            after: lastIssuePosition
          }
        });
        if (resp.data.queryIssuesV2?.length === 0) {
          setEndReached(true);
        }
      }
    } catch (error) {
      console.error("Error fetching more issues:", error);
      showAlert({
        type: "error",
        message: "We couldn\u2019t load more cases. Please try again in a moment."
      });
    }
  }, [data?.queryIssuesV2, endReached, fetchMore, showAlert]);
  const handleFilterChange = useCallback((filters: CaseListFilters) => {
    const newKey = getFilterKey(filters.showRead, filters.showUnread, filters.showClosed);
    setFiltersLoaded(prev => {
      const state = prev[newKey];
      if (state === undefined || state === FilterLoadState.NOT_LOADED) {
        return {
          ...prev,
          [newKey]: FilterLoadState.LOADING
        };
      }
      if (state === FilterLoadState.LOADING) {
        return {
          ...prev,
          [newKey]: FilterLoadState.FINISHED_LOADING
        };
      }
      return prev;
    });
    setFilters(filters);
    setEndReached(false);
  }, [setFilters]);
  const issues = data?.queryIssuesV2 ?? [];
  return <CaseList issues={issues} fetchMore={fetchMoreItems} loading={isLoading(networkStatus, currentFilterState)} showRead={showRead} showUnread={showUnread} showClosed={showClosed} onFilterChange={handleFilterChange} endReached={endReached} currentIssueId={caseId} markKeepOpen={markKeepOpen} onRefresh={doRefresh} source={source} data-sentry-element="CaseList" data-sentry-component="CasesScreenV2" data-sentry-source-file="screen.tsx" />;
};